<template>
    <div class="index-page">
        <section class="mobile-search-panel md:hidden" v-if="getUserRole === 'V2'">
            <div class="mobile-search-panel__top">
                <div>
                    <p class="mobile-search-panel__label">{{ $tt('搜索音乐') }}</p>
                    <h1>{{ $tt('搜索你想听的声音') }}</h1>
                </div>
            </div>
            <div class="search-shell">
                <span class="search-shell__icon">⌕</span>
                <input type="text" :placeholder="$tt('搜索艺术家、专辑或歌曲...')" v-model="searchTerm" @input="handleInput" autocomplete="off" @focus="showResults = true" />
            </div>
            <div v-if="showResults" class="search-results-panel">
                <div v-if="suggestions.artists && suggestions.artists.length > 0" class="search-result-group">
                    <div class="search-result-group__title">{{ $tt('艺术家') }}</div>
                    <button v-for="(artist, index) in suggestions.artists" :key="'at' + index" class="search-result-row" @click="redirectArtist(artist)">
                        <span class="search-result-row__mark">A</span>
                        <span class="search-result-row__body"><strong>{{ artist.name }}</strong><small>{{ $tt('{count}张专辑', { count: artist.albumCount }) }}</small></span>
                    </button>
                </div>
                <div v-if="suggestions.albums && suggestions.albums.length > 0" class="search-result-group">
                    <div class="search-result-group__title">{{ $tt('专辑') }}</div>
                    <button v-for="(album, index) in suggestions.albums" :key="'al' + index" class="search-result-row" @click="redirectAlbum(album)">
                        <span class="search-result-row__mark">LP</span>
                        <span class="search-result-row__body"><strong>{{ album.name }}</strong><small>{{ album.artist }} • {{ album.year }}</small></span>
                    </button>
                </div>
                <div v-if="suggestions.tracks && suggestions.tracks.length > 0" class="search-result-group">
                    <div class="search-result-group__title">{{ $tt('歌曲') }}</div>
                    <button v-for="(track, index) in suggestions.tracks" :key="'tr' + index" class="search-result-row" @click="redirectTrack(track)">
                        <span class="search-result-row__mark">♪</span>
                        <span class="search-result-row__body"><strong>{{ track.title }}</strong><small>{{ track.artist }} • {{ track.album }} • {{ track.year }}</small></span>
                    </button>
                </div>
                <div v-if="searchResultEmpty" class="search-empty"><strong>{{ $tt('未找到相关结果') }}</strong><span>{{ $tt('尝试使用不同的关键词') }}</span></div>
            </div>
        </section>

        <section class="index-hero">
            <div class="index-hero__copy">
                <p class="index-hero__label">{{ $tt('音乐总览') }}</p>
                <h1>{{ $tt('欢迎回来，{name}', { name: getUsername }) }}</h1>
                <p class="index-hero__message">{{ currentWelcome }}</p>
                <button v-if="track" class="now-playing" @click="handleRedirect('playing')"><span class="now-playing__label">{{ $tt('正在播放:') }}</span><span class="now-playing__track">{{ track.artist }} - {{ track.title }}</span></button>
            </div>
            <div class="index-hero__visual">
                <div class="hero-cover" :style="heroCoverStyle">
                    <input ref="heroCoverInput" type="file" accept="image/*" class="hero-cover__input" @change="handleHeroCoverChange">
                    <button type="button" :class="['hero-cover__stage', heroIsPlaying ? 'is-playing' : '']" :aria-label="$tt('设置专辑封面')" @click="openHeroCoverPicker">
                        <span class="hero-cover__glow" aria-hidden="true"></span>
                        <span class="hero-cover__record" aria-hidden="true">
                            <span class="hero-cover__record-label"></span>
                        </span>
                        <span class="hero-cover__art-stack">
                            <span class="hero-cover__art-card hero-cover__art-card--back">
                                <img :src="heroCoverDisplay" :alt="$tt('专辑封面')" class="hero-cover__image">
                            </span>
                            <span class="hero-cover__art-card hero-cover__art-card--middle">
                                <img :src="heroCoverDisplay" :alt="$tt('专辑封面')" class="hero-cover__image">
                            </span>
                            <span class="hero-cover__art-card hero-cover__art-card--front">
                                <img :src="heroCoverDisplay" :alt="$tt('专辑封面')" class="hero-cover__image">
                            </span>
                        </span>
                        <span class="hero-cover__tonearm" aria-hidden="true"></span>
                        <!-- <span class="hero-cover__hint">{{ $tt('点击更换封面') }}</span> -->
                    </button>
                    <div class="hero-cover__bottom">
                        <div class="hero-cover__week">
                            <span>{{ week_record.date_range || $tt('最近一周') }}</span>
                            <strong>{{ week_record.this_week_total || 0 }}</strong>
                            <small>{{ $tt('次播放') }}</small>
                        </div>
                        <div class="hero-cover__meta">
                            <span>{{ $tt('趋势') }} {{ week_record.week_rate || 0 }}%</span>
                            <span>{{ $tt('上周期') }} {{ week_record.last_week_total || 0 }}</span>
                        </div>
                        <div id="homepage-weekly" class="hero-cover__chart"></div>
                    </div>
                </div>
            </div>
        </section>

        <section class="overview-grid">
            <button v-for="card in statCards" :key="card.key" class="metric-card" :class="'metric-card--' + card.tone" @click="card.target && handleRedirect(card.target)">
                <span class="metric-card__icon">{{ card.icon }}</span><span class="metric-card__label">{{ card.label }}</span><strong :id="card.key === 'duration' ? 'example-black' : null">{{ card.value || 0 }}</strong><small>{{ card.desc }}</small>
            </button>
        </section>

        <section class="insight-grid">
            <div class="chart-panel">
                <div class="panel-heading"><div><h2>{{ $tt('音乐月度趋势') }}</h2><p>{{ $tt('歌曲、专辑和艺术家的增长走势') }}</p></div></div>
                <div id="demo" class="chart-canvas"></div>
            </div>
            <aside class="rank-panel">
                <div class="panel-heading panel-heading--inline"><div><h2>{{ $tt('排行榜') }}</h2><p>{{ $tt('最近播放记录') }}</p></div><button class="link-button" @click="handleRedirect('record')">{{ $tt('更多') }}</button></div>
                <div role="tablist" class="rank-tabs"><button :class="{ 'is-active': tabSelect === 'track' }" @click="tabSelect = 'track'">{{ $tt('歌曲') }}</button><button :class="{ 'is-active': tabSelect === 'album' }" @click="tabSelect = 'album'">{{ $tt('专辑') }}</button><button :class="{ 'is-active': tabSelect === 'artist' }" @click="tabSelect = 'artist'">{{ $tt('艺术家') }}</button></div>
                <div class="rank-list"><div v-if="activeRankList.length === 0" class="rank-empty">{{ $tt('暂无歌曲') }}</div><div v-for="(item, index) in activeRankList" :key="tabSelect + index" class="rank-row"><span class="rank-row__index">{{ index + 1 }}</span><span class="rank-row__name">{{ item.record }}</span><span class="rank-row__count">{{ $tt('{count}次', { count: item.count }) }}</span></div></div>
            </aside>
        </section>
    </div>
</template>
<script>
    import {mapGetters} from 'vuex'
    import * as culori from 'culori'
    import {getCoverArtUrl} from '@/api/apiUrl/subsonic/serializers'
    const DEFAULT_HERO_COVER = '/static/dist/img/album_null.jpg'
    const HERO_COVER_STORAGE_KEY = 'music-tag-index-hero-cover'

    export default {
        data() {
            return {
                size: 'small',
                musicCount: {},
                week_record: {},
                auditLogData: [],
                tabSelect: 'track',
                heroCoverImage: '',
                heroCoverManual: false,
                // 搜索相关数据
                searchTerm: '',
                showResults: false,
                suggestions: {},
                searchTimeout: null,
                role: '',
                welcomeMessageKeys: [
                    'index.welcome.1',
                    'index.welcome.2',
                    'index.welcome.3',
                    'index.welcome.4',
                    'index.welcome.5',
                    'index.welcome.6',
                    'index.welcome.7'
                ]
            }
        },
        computed: {
            ...mapGetters(['getUsername', 'track', 'getUserRole']),
            welcomeMessages() {
                return this.welcomeMessageKeys.map((key) => this.$tt(key))
            },
            currentWelcome() {
                const index = this.getUsername ? this.getUsername.length % this.welcomeMessages.length : 0
                return this.welcomeMessages[index] || ''
            },
            heroCoverDisplay() {
                if (this.recentTrackCover !== DEFAULT_HERO_COVER) {
                    return this.recentTrackCover
                }
                if (this.heroCoverManual && this.heroCoverImage) {
                    return this.heroCoverImage
                }
                return DEFAULT_HERO_COVER
            },
            recentTrackCover() {
                const cover = this.track && (this.track.image || this.track.coverArt || this.track.artwork)
                return this.normalizeHeroCover(cover)
            },
            heroCoverStyle() {
                return {
                    '--hero-cover': `url(${JSON.stringify(this.heroCoverDisplay)})`
                }
            },
            heroIsPlaying() {
                return this.$store && this.$store.state && this.$store.state.common && this.$store.state.common.isPlaying
            },
            searchResultEmpty() {
                const artists = this.suggestions.artists || []
                const albums = this.suggestions.albums || []
                const tracks = this.suggestions.tracks || []
                return this.searchTerm && artists.length === 0 && albums.length === 0 && tracks.length === 0
            },
            statCards() {
                return [
                    { key: 'tracks', icon: 'TR', label: this.$tt('全部歌曲'), value: this.musicCount.track_count, desc: this.$tt('{percent}% 较上月变化', { percent: this.musicCount.track_mom || 0 }), target: 'tracks', tone: 'primary' },
                    { key: 'albums', icon: 'LP', label: this.$tt('全部专辑'), value: this.musicCount.album_count, desc: `${this.musicCount.album_diff || 0} (${this.musicCount.album_mom || 0}%)`, target: 'albums', tone: 'accent' },
                    { key: 'artists', icon: 'AR', label: this.$tt('全部艺术家'), value: this.musicCount.artist_count, desc: `${this.musicCount.artist_diff || 0} (${this.musicCount.artist_mom || 0}%)`, target: 'artists', tone: 'primary' },
                    { key: 'size', icon: 'GB', label: this.$tt('歌曲大小'), value: this.musicCount.song_size, desc: this.$tt('{percent}% 较上月变化', { percent: this.musicCount.track_mom || 0 }), target: '', tone: 'neutral' },
                    { key: 'progress', icon: 'OK', label: this.$tt('刮削完整度'), value: this.musicCount.finished_rate, desc: this.$tt('点击我查看详情'), target: 'progress', tone: 'accent' },
                    { key: 'duration', icon: 'TM', label: this.$tt('歌曲时长'), value: this.musicCount.song_duration, desc: this.$tt('总时长 {seconds}s', { seconds: this.musicCount.song_duration_raw || 0 }), target: '', tone: 'neutral' }
                ]
            },
            activeRankList() {
                if (this.tabSelect === 'album') return this.auditLogData.album_list || []
                if (this.tabSelect === 'artist') return this.auditLogData.artist_list || []
                return this.auditLogData.track_list || []
            }
        },
        created() {
            this.loadHeroCoverPreference()
            this.fetchMusicCount()
            this.fetchWeekRecord()
            this.fetchAuditLog()
        },
        mounted() {
            if (typeof window !== 'undefined') {
                window.addEventListener('mtw-locale-change', this.handleLocaleChange)
            }
        },
        beforeDestroy() {
            if (typeof window !== 'undefined') {
                window.removeEventListener('mtw-locale-change', this.handleLocaleChange)
            }
        },
        methods: {
            oklchToHex(oklchString) {
                if (!oklchString || !oklchString.startsWith('oklch')) {
                    return oklchString
                }
                const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+)?\)/)
                if (!match) {
                    return oklchString
                }

                const [, l, c, h] = match.map(Number)

                // 使用culori将OKLCH转换为RGB
                const rgb = culori.converter('rgb')({mode: 'oklch', l, c, h})

                // 将RGB值限制在0-1范围内，然后转换为0-255范围的整数
                const clamp = (value) => Math.max(0, Math.min(1, value))
                const r = Math.round(clamp(rgb.r) * 255)
                const g = Math.round(clamp(rgb.g) * 255)
                const b = Math.round(clamp(rgb.b) * 255)

                // 将RGB转换为16进制
                const toHex = (value) => {
                    const hex = value.toString(16)
                    return hex.length === 1 ? '0' + hex : hex
                }

                return '#' + toHex(r) + toHex(g) + toHex(b)
            },
            handleLocaleChange() {
                if (this.musicCount && this.musicCount.trends) {
                    this.draw()
                }
                if (this.week_record && this.week_record.record) {
                    this.drawWeekly()
                }
            },
            handlePageChange(page) {
                this.pagination.current = page
            },
            loadHeroCoverPreference() {
                if (typeof window === 'undefined' || !window.localStorage) {
                    return
                }
                try {
                    const savedCover = window.localStorage.getItem(HERO_COVER_STORAGE_KEY)
                    this.heroCoverImage = savedCover || ''
                    this.heroCoverManual = Boolean(savedCover)
                } catch (error) {
                    this.heroCoverImage = ''
                    this.heroCoverManual = false
                }
            },
            openHeroCoverPicker() {
                if (this.$refs.heroCoverInput) {
                    this.$refs.heroCoverInput.click()
                }
            },
            normalizeHeroCover(cover) {
                if (!cover) {
                    return DEFAULT_HERO_COVER
                }
                if (/^(al|ar|at|pl)-/.test(String(cover))) {
                    return getCoverArtUrl({coverArt: cover}) || DEFAULT_HERO_COVER
                }
                return cover
            },
            handleHeroCoverChange(event) {
                const file = event.target.files && event.target.files[0]
                if (!file) {
                    return
                }
                const reader = new FileReader()
                reader.onload = () => {
                    this.heroCoverImage = reader.result || DEFAULT_HERO_COVER
                    this.heroCoverManual = true
                    if (typeof window !== 'undefined' && window.localStorage) {
                        try {
                            window.localStorage.setItem(HERO_COVER_STORAGE_KEY, this.heroCoverImage)
                        } catch (error) {
                            window.localStorage.removeItem(HERO_COVER_STORAGE_KEY)
                        }
                    }
                }
                reader.readAsDataURL(file)
                event.target.value = ''
            },
            remove(row) {
                const index = this.data.indexOf(row)
                if (index !== -1) {
                    this.data.splice(index, 1)
                }
            },
            draw() {
                const chartDom = document.getElementById('demo')
                if (!chartDom) {
                    return
                }
                const myChart = this.$echarts.getInstanceByDom(chartDom) || this.$echarts.init(chartDom)
                let accentColor = window.getComputedStyle(document.getElementById('example-black'))['color']
                accentColor = this.oklchToHex(accentColor)
                const trendTitle = this.$tt('音乐月度趋势')
                const trackLabel = this.$tt('歌曲')
                const albumLabel = this.$tt('专辑')
                const artistLabel = this.$tt('艺术家')
                const option = {
                    color: ['#80FFA5', '#00DDFF', '#37A2FF', '#FF0087', '#FFBF00'],
                    title: {
                        text: trendTitle,
                        textStyle: {
                            color: accentColor
                        }
                    },
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: {
                            type: 'cross',
                            label: {
                                backgroundColor: '#6a7985'
                            }
                        }
                    },
                    legend: {
                        data: [trackLabel, albumLabel, artistLabel],
                        textStyle: {
                            color: accentColor
                        }
                    },
                    toolbox: {
                        feature: {
                            saveAsImage: {}
                        }
                    },
                    grid: {
                        left: '3%',
                        right: '4%',
                        bottom: '3%',
                        containLabel: true
                    },
                    xAxis: [
                        {
                            type: 'category',
                            boundaryGap: false,
                            data: this.musicCount.trends.x_axis
                        }
                    ],
                    yAxis: [
                        {
                            type: 'value'
                        }
                    ],
                    series: [
                        {
                            name: trackLabel,
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(128, 255, 165)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(1, 191, 236)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.track
                        },
                        {
                            name: albumLabel,
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(0, 221, 255)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(77, 119, 255)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.album
                        },
                        {
                            name: artistLabel,
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(55, 162, 255)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(116, 21, 219)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.artist
                        }
                    ]
                }
                myChart.setOption(option)
            },
            drawWeekly() {
                const chartDom = document.getElementById('homepage-weekly')
                if (!chartDom || !this.week_record || !this.week_record.record) {
                    return
                }
                const myChart = this.$echarts.getInstanceByDom(chartDom) || this.$echarts.init(chartDom)
                const colorNode = document.getElementById('example-black')
                let accentColor = colorNode ? window.getComputedStyle(colorNode)['color'] : '#5f46e8'
                accentColor = this.oklchToHex(accentColor)
                const option = {
                    animationDuration: 600,
                    color: ['rgba(255, 255, 255, 0.92)', 'rgba(255, 255, 255, 0.34)'],
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: {
                            type: 'shadow'
                        }
                    },
                    legend: {
                        show: false
                    },
                    dataset: {
                        source: this.week_record.record
                    },
                    grid: {
                        left: 0,
                        right: 0,
                        top: 18,
                        bottom: 4,
                        containLabel: false
                    },
                    xAxis: {
                        type: 'category',
                        axisLine: {
                            show: false
                        },
                        axisTick: {
                            show: false
                        },
                        axisLabel: {
                            color: 'rgba(255, 255, 255, 0.68)',
                            interval: 0,
                            fontSize: 10
                        }
                    },
                    yAxis: [{
                        type: 'value',
                        show: false
                    }],
                    series: [
                        {
                            type: 'bar',
                            barWidth: 12,
                            itemStyle: {
                                borderRadius: [8, 8, 0, 0]
                            }
                        },
                        {
                            type: 'bar',
                            barWidth: 12,
                            itemStyle: {
                                borderRadius: [8, 8, 0, 0]
                            }
                        }
                    ]
                }
                myChart.setOption(option)
            },
            fetchMusicCount() {
                this.$api.Task.getMusicCount().then(res => {
                    if (res.result) {
                        this.musicCount = res.data
                        this.$nextTick(() => {
                            this.draw()
                            this.drawWeekly()
                        })
                        if (this.musicCount.track_count === 0) {
                            this.$myMsg.notify({
                            title: this.$tt('暂无歌曲'),
                            content: this.$tt('请从操作台-导入收藏中添加音乐吧！'),
                            type: 'warning',
                            time: 2000,
                            url: '/'
                        });
                        }
                    }
                })
            },
            fetchWeekRecord() {
                this.$api.Task.WeekRecord({period: 'week'}).then((res) => {
                    if (res.result) {
                        this.week_record = res.data
                        this.$nextTick(() => {
                            this.drawWeekly()
                        })
                    }
                })
            },
            fetchAuditLog() {
                this.$api.Task.getListeningRecord({page_size: 8}).then(res => {
                    if (res.result) {
                        this.auditLogData = res.data
                    }
                })
            },
            handleInput() {
                if (this.searchTerm.length > 0) {
                    // 发送AJAX请求获取联想结果
                    if (this.searchTimeout) {
                        clearTimeout(this.searchTimeout)
                    }
                    this.searchTimeout = setTimeout(() => {
                        this.searchTimeout = null
                        this.$api.subsonic.search(this.searchTerm).then((res) => {
                            this.suggestions = res
                            this.showResults = true
                        }).catch(() => {
                            this.suggestions = {}
                            this.showResults = false
                        })
                    }, 300)
                } else {
                    this.suggestions = {}
                    this.showResults = false
                }
            },
            redirectArtist(item) {
                this.$router.push({ path: '/artist/id/' + item.id })
                this.showResults = false
                this.searchTerm = ''
            },
            redirectAlbum(item) {
                this.$router.push({ path: '/album/id/' + item.id })
                this.showResults = false
                this.searchTerm = ''
            },
            redirectTrack(item) {
                this.$router.push({ path: '/album/id/' + item.albumId })
                this.showResults = false
                this.searchTerm = ''
            },
            handleRedirect(v) {
                if (v === 'tracks') {
                    this.$router.push({path: '/tracks/recently-added/'})
                } else if (v === 'albums') {
                    this.$router.push({path: '/albums/recently-added/'})
                } else if (v === 'artists') {
                    this.$router.push({path: '/artists/most-albums/'})
                } else if (v === 'playing') {
                    this.$router.push({path: '/playing'})
                } else if (v === 'progress') {
                    this.$router.push({path: '/progress'})
                } else if (v === 'record') {
                    this.$router.push({path: '/record'})
                }
            }
        }
    }
</script>
<style lang="postcss" scoped>
.index-page { min-height: calc(100dvh - 4rem); padding: 1rem; overflow-y: auto; color: oklch(var(--bc)); background: radial-gradient(circle at 9% 0%, oklch(var(--p) / 0.14), transparent 28rem), radial-gradient(circle at 92% 12%, oklch(var(--a) / 0.12), transparent 24rem), linear-gradient(135deg, oklch(var(--b1)), oklch(var(--b2))); }
.index-page > section { width: 100%; max-width: 1480px; margin-left: auto; margin-right: auto; }
.mobile-search-panel, .index-hero, .metric-card, .chart-panel, .rank-panel { border: 1px solid oklch(var(--bc) / 0.08); border-radius: 1.5rem; background: linear-gradient(135deg, oklch(var(--b1) / 0.92), oklch(var(--b2) / 0.7)), oklch(var(--b1)); box-shadow: inset 0 1px 0 oklch(var(--b1) / 0.78), 0 20px 48px oklch(var(--bc) / 0.09); }
.mobile-search-panel { margin-bottom: 1rem; padding: 1.1rem; }
.mobile-search-panel__top { display: flex; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.mobile-search-panel__label, .index-hero__label { margin: 0 0 0.35rem; font-size: 0.8rem; font-weight: 800; color: oklch(var(--p)); }
.mobile-search-panel h1 { margin: 0; color: oklch(var(--bc)); font-size: 2rem; line-height: 1; font-weight: 800; letter-spacing: -0.04em; }
.search-shell { display: flex; align-items: center; gap: 0.75rem; border-radius: 999px; padding: 0.82rem 1rem; color: oklch(var(--bc)); background: oklch(var(--b1) / 0.86); box-shadow: 0 12px 30px oklch(var(--bc) / 0.08); }
.search-shell__icon { width: 1.25rem; text-align: center; color: oklch(var(--p)); font-weight: 900; }
.search-shell input { min-width: 0; width: 100%; border: 0; outline: 0; background: transparent; color: oklch(var(--bc)); font-size: 0.98rem; }
.search-shell input::placeholder { color: oklch(var(--bc) / 0.48); }
.search-results-panel { margin-top: 0.85rem; max-height: 20rem; overflow-y: auto; border-radius: 1.25rem; padding: 0.5rem; background: oklch(var(--b1) / 0.78); }
.search-result-group + .search-result-group { margin-top: 0.5rem; }
.search-result-group__title { padding: 0.55rem 0.75rem 0.35rem; color: oklch(var(--bc) / 0.58); font-size: 0.78rem; font-weight: 800; }
.search-result-row { width: 100%; display: flex; align-items: center; gap: 0.75rem; border: 0; border-radius: 1rem; padding: 0.65rem; color: oklch(var(--bc)); background: transparent; text-align: left; cursor: pointer; transition: transform 0.18s ease, background-color 0.18s ease; }
.search-result-row:hover { background: oklch(var(--p) / 0.08); }
.search-result-row:active { transform: translateY(1px); }
.search-result-row__mark { flex: 0 0 2.25rem; height: 2.25rem; border-radius: 0.9rem; display: inline-flex; align-items: center; justify-content: center; color: oklch(var(--p)); background: oklch(var(--p) / 0.1); font-size: 0.78rem; font-weight: 900; }
.search-result-row__body { min-width: 0; display: grid; gap: 0.1rem; }
.search-result-row__body strong, .search-result-row__body small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.search-result-row__body small, .search-empty span { color: oklch(var(--bc) / 0.55); }
.search-empty { display: grid; gap: 0.35rem; place-items: center; padding: 2rem 1rem; color: oklch(var(--bc)); text-align: center; }
.index-hero { min-height: 16.5rem; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(18rem, 0.85fr); align-items: stretch; gap: 1rem; overflow: hidden; padding: 1.15rem 1.15rem 1.15rem 1.45rem; }
.index-hero__copy { display: flex; flex-direction: column; justify-content: center; min-width: 0; }
.index-hero h1 { max-width: 10ch; margin: 0; color: oklch(var(--bc)); font-size: clamp(2.35rem, 5vw, 4.9rem); line-height: 0.96; font-weight: 850; letter-spacing: -0.04em; }
.index-hero__message { max-width: 42rem; margin: 1rem 0 0; color: oklch(var(--bc) / 0.62); font-size: 1rem; line-height: 1.7; }
.now-playing { align-self: flex-start; max-width: 100%; display: inline-flex; align-items: center; gap: 0.65rem; margin-top: 1.25rem; border: 0; border-radius: 999px; padding: 0.75rem 1rem; color: oklch(var(--pc)); background: oklch(var(--p)); cursor: pointer; box-shadow: 0 16px 34px oklch(var(--p) / 0.18); transition: transform 0.18s ease, box-shadow 0.18s ease; }
.now-playing:hover { transform: translateY(-1px); box-shadow: 0 18px 38px oklch(var(--p) / 0.24); }
.now-playing:active, .metric-card:active, .link-button:active, .rank-tabs button:active { transform: translateY(1px); }
.now-playing__label { flex: 0 0 auto; opacity: 0.72; }
.now-playing__track { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 800; }
.index-hero__visual { position: relative; min-height: 15rem; border-radius: 1.25rem; overflow: hidden; background: radial-gradient(circle at 20% 10%, oklch(var(--p) / 0.46), transparent 12rem), radial-gradient(circle at 96% 18%, oklch(var(--a) / 0.28), transparent 11rem), linear-gradient(145deg, oklch(var(--bc) / 0.94), oklch(var(--bc) / 0.68)); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16), 0 24px 58px oklch(var(--bc) / 0.16); }
.hero-cover { position: relative; min-height: 100%; display: grid; grid-template-rows: minmax(14.5rem, 1fr) auto; gap: 0.2rem; padding: 1rem; color: rgba(255, 255, 255, 0.92); isolation: isolate; overflow: visible; }
.hero-cover::before { content: ""; position: absolute; inset: -22%; z-index: -2; background-image: var(--hero-cover); background-size: cover; background-position: center; opacity: 0.2; filter: blur(42px) saturate(1.16); transform: scale(1.05); }
.hero-cover::after { content: ""; position: absolute; inset: 0; z-index: -1; background: linear-gradient(135deg, rgba(10, 10, 18, 0.78), rgba(35, 22, 54, 0.58)), radial-gradient(circle at 60% 42%, rgba(255, 255, 255, 0.14), transparent 10rem); pointer-events: none; }
.hero-cover__input { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.hero-cover__stage { position: relative; z-index: 2; width: 100%; min-width: 0; min-height: 14.5rem; margin: 0 auto -2.1rem; border: 0; padding: 0; display: flex; align-items: center; justify-content: center; color: inherit; background: transparent; cursor: pointer; perspective: 1100px; }
.hero-cover__stage:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.72); outline-offset: 0.25rem; border-radius: 1rem; }
.hero-cover__glow { position: absolute; width: clamp(15rem, 88%, 21rem); aspect-ratio: 1; border-radius: 999px; background-image: var(--hero-cover); background-size: cover; background-position: center; opacity: 0.36; filter: blur(28px) saturate(1.18); }
.hero-cover__record { position: absolute; width: clamp(15rem, 88%, 21rem); aspect-ratio: 1; border-radius: 999px; transform: translateX(15%) rotate(0deg); background: radial-gradient(circle at center, rgba(255, 255, 255, 0.88) 0 7.6%, transparent 8%), repeating-radial-gradient(circle at center, rgba(255, 255, 255, 0.14) 0 1px, transparent 1px 8px), radial-gradient(circle at center, rgba(18, 18, 24, 0.98) 0 62%, rgba(5, 5, 10, 0.96) 100%); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 0 34px rgba(0, 0, 0, 0.58), 0 22px 48px rgba(0, 0, 0, 0.24); opacity: 0.82; transition: transform 460ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease, filter 300ms ease; }
.hero-cover__record::before { content: ""; position: absolute; inset: 8%; border-radius: inherit; border: 1px solid rgba(255, 255, 255, 0.08); background: conic-gradient(from 18deg, transparent 0 38deg, rgba(255, 255, 255, 0.1) 39deg 48deg, transparent 49deg 100deg, rgba(255, 255, 255, 0.08) 101deg 108deg, transparent 109deg 360deg); mix-blend-mode: screen; opacity: 0.74; }
.hero-cover__record-label { position: absolute; inset: 38%; border-radius: inherit; background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.88) 0 12%, transparent 13%), linear-gradient(135deg, oklch(var(--p) / 0.72), rgba(255, 255, 255, 0.16)); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.34), 0 0 0 1px rgba(255, 255, 255, 0.18); }
.hero-cover__art-stack { position: relative; z-index: 4; width: clamp(15rem, 88%, 21rem); aspect-ratio: 1; flex: 0 0 auto; border-radius: 1.25rem; transform: rotateX(5deg) rotateY(-8deg); transform-style: preserve-3d; transition: transform 460ms cubic-bezier(0.16, 1, 0.3, 1), filter 300ms ease; }
.hero-cover__art-card { position: absolute; inset: 0; border-radius: inherit; overflow: hidden; background: rgba(255, 255, 255, 0.1); box-shadow: 0 20px 48px rgba(0, 0, 0, 0.18); }
.hero-cover__art-card--back { z-index: 1; opacity: 0.28; transform: translate3d(1.15rem, 1.15rem, -3rem) rotate(4.5deg) scale(0.98); }
.hero-cover__art-card--middle { z-index: 2; opacity: 0.56; transform: translate3d(0.58rem, 0.58rem, -1.5rem) rotate(2.2deg) scale(0.99); }
.hero-cover__art-card--front { z-index: 3; opacity: 1; box-shadow: 0 28px 70px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.22); }
.hero-cover__image { position: relative; z-index: 1; width: 100%; height: 100%; border-radius: inherit; object-fit: cover; display: block; }
.hero-cover__tonearm { position: absolute; top: 13%; right: 13%; z-index: 3; width: 30%; height: 0.38rem; border-radius: 999px; transform-origin: 88% 50%; transform: rotate(-22deg) translateX(0.8rem); background: linear-gradient(90deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0.62)); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.32), 0 10px 24px rgba(0, 0, 0, 0.2); opacity: 0.56; transition: transform 520ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease; }
.hero-cover__tonearm::before, .hero-cover__tonearm::after { content: ""; position: absolute; border-radius: 999px; }
.hero-cover__tonearm::before { right: -0.45rem; top: 50%; width: 1.35rem; height: 1.35rem; transform: translateY(-50%); background: rgba(255, 255, 255, 0.68); box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18); }
.hero-cover__tonearm::after { left: -0.35rem; top: 50%; width: 0.9rem; height: 0.9rem; transform: translateY(-50%); background: rgba(255, 255, 255, 0.5); }
.hero-cover__hint { position: absolute; left: 0.1rem; bottom: 0.1rem; border-radius: 999px; padding: 0.42rem 0.7rem; color: rgba(255, 255, 255, 0.72); background: rgba(255, 255, 255, 0.12); font-size: 0.76rem; font-weight: 820; backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); }
.hero-cover__stage:hover .hero-cover__art-stack { transform: rotateX(6deg) rotateY(-10deg) translateY(-0.2rem); }
.hero-cover__stage:hover .hero-cover__record { transform: translateX(18%) rotate(10deg); opacity: 0.9; }
.hero-cover__stage:hover .hero-cover__tonearm, .hero-cover__stage.is-playing .hero-cover__tonearm { opacity: 0.74; transform: rotate(-13deg) translateX(0.55rem); }
.hero-cover__stage.is-playing .hero-cover__record { animation: hero-cover-record-spin 9s linear infinite; }
.hero-cover__stage.is-playing .hero-cover__art-stack { transform: rotateX(5deg) rotateY(-8deg) translate3d(-0.45rem, -0.2rem, 0); animation: hero-cover-cover-breathe 3.6s ease-in-out infinite; }
.hero-cover__bottom { position: relative; z-index: 1; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 0.6rem; align-items: end; padding-top: 1.4rem; }
.hero-cover__week { min-width: 5.8rem; display: grid; gap: 0.05rem; }
.hero-cover__week span, .hero-cover__meta span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: rgba(255, 255, 255, 0.68); font-size: 0.76rem; font-weight: 780; }
.hero-cover__week strong { color: rgba(255, 255, 255, 0.96); font-size: clamp(2.2rem, 4.2vw, 3.8rem); line-height: 0.9; font-weight: 880; letter-spacing: -0.06em; }
.hero-cover__week small { color: rgba(255, 255, 255, 0.68); font-size: 0.8rem; font-weight: 820; }
.hero-cover__meta { display: flex; justify-content: flex-end; gap: 0.42rem; min-width: 0; }
.hero-cover__meta span { max-width: 8.5rem; border-radius: 999px; padding: 0.34rem 0.58rem; background: rgba(255, 255, 255, 0.12); }
.hero-cover__chart { grid-column: 1 / -1; width: 100%; min-height: 3.55rem; }
@keyframes hero-cover-record-spin { to { transform: translateX(13%) rotate(360deg); } }
@keyframes hero-cover-cover-breathe { 0%, 100% { filter: saturate(1); } 50% { filter: saturate(1.08) brightness(1.04); } }
.overview-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 0.85rem; margin-top: 0.9rem; }
.metric-card { min-height: 9.8rem; display: flex; flex-direction: column; align-items: flex-start; justify-content: space-between; padding: 1rem; color: oklch(var(--bc)); text-align: left; cursor: pointer; transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease; }
.metric-card:hover { transform: translateY(-2px); border-color: oklch(var(--p) / 0.24); box-shadow: 0 22px 50px oklch(var(--bc) / 0.12); }
.metric-card__icon { display: inline-flex; align-items: center; justify-content: center; width: 2.4rem; height: 2.4rem; border-radius: 0.95rem; font-size: 0.72rem; font-weight: 900; color: oklch(var(--p)); background: oklch(var(--p) / 0.1); }
.metric-card--accent .metric-card__icon { color: oklch(var(--a)); background: oklch(var(--a) / 0.11); }
.metric-card--neutral .metric-card__icon { color: oklch(var(--bc) / 0.68); background: oklch(var(--bc) / 0.08); }
.metric-card__label { margin-top: 0.8rem; color: oklch(var(--bc) / 0.58); font-size: 0.82rem; font-weight: 750; }
.metric-card strong { width: 100%; color: oklch(var(--bc)); font-size: clamp(1.55rem, 2.5vw, 2.4rem); line-height: 1.05; font-weight: 850; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.metric-card small { max-width: 100%; color: oklch(var(--bc) / 0.52); font-size: 0.78rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.insight-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(20rem, 24rem); gap: 0.9rem; margin-top: 0.9rem; min-height: 27rem; }
.chart-panel, .rank-panel { min-width: 0; padding: 1.15rem; }
.panel-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 0.7rem; }
.panel-heading--inline { align-items: center; }
.panel-heading h2 { margin: 0; color: oklch(var(--bc)); font-size: 1.18rem; line-height: 1.2; font-weight: 850; }
.panel-heading p { margin: 0.25rem 0 0; color: oklch(var(--bc) / 0.54); font-size: 0.88rem; }
.chart-canvas { width: 100%; height: 23rem; }
.link-button { flex: 0 0 auto; border: 0; border-radius: 999px; padding: 0.55rem 0.85rem; color: oklch(var(--p)); background: oklch(var(--p) / 0.1); font-weight: 800; cursor: pointer; }
.rank-tabs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.35rem; border-radius: 999px; padding: 0.35rem; background: oklch(var(--bc) / 0.07); }
.rank-tabs button { border: 0; border-radius: 999px; padding: 0.58rem 0.65rem; color: oklch(var(--bc) / 0.58); background: transparent; font-weight: 800; cursor: pointer; }
.rank-tabs button.is-active { color: oklch(var(--pc)); background: oklch(var(--p)); }
.rank-list { display: grid; gap: 0.55rem; margin-top: 0.9rem; }
.rank-row, .rank-empty { border-radius: 1rem; background: oklch(var(--b1) / 0.58); }
.rank-row { display: grid; grid-template-columns: 2.1rem minmax(0, 1fr) auto; align-items: center; gap: 0.65rem; min-height: 3.2rem; padding: 0.65rem 0.75rem; }
.rank-row__index { width: 2.1rem; height: 2.1rem; border-radius: 0.78rem; display: grid; place-items: center; color: oklch(var(--p)); background: oklch(var(--p) / 0.1); font-size: 0.82rem; font-weight: 900; }
.rank-row__name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: oklch(var(--bc)); font-weight: 760; }
.rank-row__count { color: oklch(var(--bc) / 0.52); font-size: 0.82rem; white-space: nowrap; }
.rank-empty { padding: 2rem 1rem; color: oklch(var(--bc) / 0.56); text-align: center; }
#example-black { color: oklch(var(--p)); }
@media (prefers-reduced-motion: reduce) { .search-result-row, .now-playing, .metric-card, .link-button, .rank-tabs button, .hero-cover__art-stack, .hero-cover__record, .hero-cover__tonearm { transition: none; } .hero-cover__stage.is-playing .hero-cover__record, .hero-cover__stage.is-playing .hero-cover__art-stack { animation: none; } }
@media (max-width: 1279px) { .overview-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } .insight-grid { grid-template-columns: 1fr; } }
@media (max-width: 767px) { .index-page { min-height: auto; padding: 0.75rem 0.65rem 1.25rem; background: radial-gradient(circle at 14% 0%, oklch(var(--p) / 0.12), transparent 18rem), linear-gradient(135deg, oklch(var(--b1)), oklch(var(--b2))); } .index-hero { min-height: auto; grid-template-columns: 1fr; gap: 1rem; padding: 0.95rem; border-radius: 1.4rem; } .index-hero h1 { max-width: 12ch; font-size: 2.25rem; } .index-hero__message { font-size: 0.95rem; } .index-hero__visual { min-height: 20rem; } .hero-cover { grid-template-rows: minmax(13.5rem, 1fr) auto; padding: 0.85rem; } .hero-cover__glow, .hero-cover__record, .hero-cover__art-stack { width: clamp(14rem, 84%, 16.5rem); } .hero-cover__stage { min-height: 13.5rem; margin-bottom: -1.6rem; } .hero-cover__bottom { grid-template-columns: 1fr; padding-top: 0.9rem; } .hero-cover__meta { justify-content: flex-start; } .overview-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.65rem; } .metric-card { min-height: 8.8rem; border-radius: 1.25rem; } .metric-card strong { font-size: 1.45rem; } .chart-panel, .rank-panel { border-radius: 1.35rem; padding: 0.9rem; } .chart-canvas { height: 19rem; } .panel-heading h2 { font-size: 1.06rem; } }
</style>
